import {
  DeleteOutlined,
  EditTwoTone,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Button, Flex, List } from "antd";
import { useCallback, useMemo } from "react";

type Props<T> = {
  addButtonDisabled?: boolean;
  addButtonText: string;
  editing?: boolean;
  items: T[];
  onAddItem: () => void;
  onDeleteItem: (index: number) => void;
  onEditItem: (index: number) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
};

export const EditableList = <T,>({
  addButtonDisabled = false,
  addButtonText,
  editing = false,
  items,
  onAddItem,
  onDeleteItem,
  onEditItem,
  renderItem,
}: Props<T>) => {
  const deleteCallbacks = useMemo(
    () => items.map((_item, ix) => () => onDeleteItem(ix)),
    [items, onDeleteItem]
  );
  const editCallbacks = useMemo(
    () => items.map((_item, ix) => () => onEditItem(ix)),
    [items, onEditItem]
  );

  const renderEditableItem = useCallback(
    (item: T, index: number) => (
      <List.Item
        className="border-be-0! py-1!"
        actions={
          editing
            ? []
            : [
                <Button
                  key={`${index}-0`}
                  type="text"
                  onClick={editCallbacks[index]}
                  icon={<EditTwoTone />}
                />,
                <Button
                  key={`${index}-1`}
                  type="text"
                  danger
                  onClick={deleteCallbacks[index]}
                  icon={<DeleteOutlined />}
                />,
              ]
        }
      >
        <Flex justify="space-between" flex="1">
          {renderItem(item, index)}
        </Flex>
      </List.Item>
    ),
    [deleteCallbacks, editCallbacks, editing, renderItem]
  );

  return (
    <Flex vertical gap={8} align="flex-start">
      {items.length > 0 && (
        <List<T>
          dataSource={items}
          renderItem={renderEditableItem}
          split={false}
          className="w-full"
        />
      )}
      {!editing && (
        <Button
          type="link"
          size="small"
          onClick={onAddItem}
          icon={<PlusCircleOutlined />}
          disabled={addButtonDisabled}
          className="px-0!"
        >
          {addButtonText}
        </Button>
      )}
    </Flex>
  );
};
